// src/pages/AddFarmPage1.jsx

import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  Typography,
  InputAdornment,
  Button,
  Tooltip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import BASE_URL from "../../config/apiConfig";
import { getUserEmail } from "../../utils/auth";

import AgricultureIcon from "@mui/icons-material/Agriculture";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import "./AddFarmPage1.css";

/* global google */

const AddFarmPage1 = () => {
  const navigate = useNavigate();

  const mapRef = useRef(null);
  const searchBoxRef = useRef(null);
  const polygonCoords = useRef([]);
  const polygonRef = useRef(null);
  const map = useRef(null);

  const DEFAULT_LOCATION = { lat: 37.7749, lng: -122.4194 };

  const [farmName, setFarmName] = useState("");
  const [sowingDate, setSowingDate] = useState("");
  const [cropType, setCropType] = useState("plantation");
  const [crops, setCrops] = useState([]);
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [selectedArea, setSelectedArea] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [SearchErrorMessage, setSearchErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [paymentFrequency, setPaymentFrequency] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [hovered, setHovered] = useState(false);
  const [spinnerLoading, setSpinnerLoading] = useState(false);

  useEffect(() => {
    const loadGoogleMapsScript = () => {
      const script = document.createElement("script");

      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=geometry,places,marker`;
      script.async = true;
      script.defer = true;

      script.onload = () =>
        window.google
          ? initMap()
          : setErrorMessage("Google Maps failed to load.");

      script.onerror = () =>
        setErrorMessage("Failed to load Google Maps.");

      document.body.appendChild(script);

      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    };

    loadGoogleMapsScript();
  }, []);

  const initMap = () => {
    getUserLocation()
      .then((loc) => initializeMap(loc))
      .catch(() => initializeMap(DEFAULT_LOCATION));
  };

  const getUserLocation = () =>
    new Promise((resolve, reject) => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (pos) =>
            resolve({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            }),
          async () => {
            try {
              const ipLoc = await axios.get("https://ipapi.co/json/");
              resolve({
                lat: ipLoc.data.latitude,
                lng: ipLoc.data.longitude,
              });
            } catch {
              reject();
            }
          }
        );
      } else {
        reject();
      }
    });

  const initializeMap = (center) => {
    map.current = new google.maps.Map(mapRef.current, {
      center,
      zoom: 15,
      mapTypeId: google.maps.MapTypeId.HYBRID,
      mapId: "520ede0eb4624c69",
    });

    polygonRef.current = new google.maps.Polygon({
      paths: polygonCoords.current,
      strokeColor: "#FFD21F",
      strokeOpacity: 1,
      strokeWeight: 4,
      fillColor: "#8BC34A",
      fillOpacity: 0.22,
      geodesic: true,
    });

    polygonRef.current.setMap(map.current);

    map.current.addListener("click", (e) => {
      addMarker(e.latLng);
      addPolygonPoint(e.latLng);
    });

    const input = searchBoxRef.current;
    const searchBox = new google.maps.places.SearchBox(input);

    searchBox.addListener("places_changed", () => {
      const places = searchBox.getPlaces();
      if (!places.length) return;

      const bounds = new google.maps.LatLngBounds();

      places.forEach((place) => {
        if (place.geometry?.location) {
          bounds.extend(place.geometry.location);
          map.current.setCenter(place.geometry.location);
          map.current.setZoom(15);
        }
      });

      map.current.fitBounds(bounds);
    });
  };

  const addMarker = (location) => {
    map.current.markers ||= [];

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: map.current,
      position: location,
    });

    map.current.markers.push(marker);
  };

  const addPolygonPoint = (location) => {
    polygonCoords.current.push({
      lat: location.lat(),
      lng: location.lng(),
    });

    polygonRef.current.setPath(polygonCoords.current);
    updateArea();
  };

  const updateArea = () => {
    if (polygonCoords.current.length < 3) {
      return setSelectedArea(0);
    }

    const polygon = new google.maps.Polygon({
      paths: polygonCoords.current,
    });

    const sqm = google.maps.geometry.spherical.computeArea(
      polygon.getPath()
    );

    setSelectedArea((sqm / 10000).toFixed(2));
  };

  const handleGeocode = () => {
    const geocoder = new google.maps.Geocoder();

    geocoder.geocode(
      { address: searchBoxRef.current.value },
      (results, status) => {
        if (status === "OK" && results[0]) {
          const loc = results[0].geometry.location;

          map.current.setCenter(loc);
          addMarker(loc);
        } else {
          setSearchErrorMessage(
            "Location not found. Please enter a precise address or select one from the suggestions."
          );
        }
      }
    );
  };

  const clearAll = () => {
    map.current?.markers?.forEach((m) => (m.map = null));

    if (map.current) {
      map.current.markers = [];
    }

    polygonCoords.current = [];
    polygonRef.current?.setPath([]);

    setSelectedArea(0);
    setErrorMessage("");
    setTotalPrice(0);
  };

  useEffect(() => {
    const fetchCrops = async () => {
      if (!cropType) return;

      setLoading(true);

      try {
        const res = await fetch(
          `${BASE_URL}/api/${
            cropType === "plantation"
              ? "plantation-crops"
              : "field-crops"
          }`
        );

        const data = await res.json();

        const formatted = data.map((c) => ({
          id: c.crop_id,
          name: c.crop_name,
          perHectarePrice: c.per_hectare_price,
        }));

        setCrops(formatted);
        setSelectedCrop(formatted[0] || null);
      } catch {
        setCrops([]);
        setSelectedCrop(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCrops();
  }, [cropType]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleCalculate = async () => {
    if (!selectedCrop) return;

    try {
      const response = await fetch(`${BASE_URL}/api/calculate-price`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          landSize: selectedArea,
          perHectarePrice: selectedCrop.perHectarePrice,
        }),
      });

      if (!response.ok) throw new Error("Failed to calculate price");

      const data = await response.json();
      let finalPrice = data.calculatedPrice;

      if (paymentFrequency === 3) finalPrice *= 3 * 0.9;
      if (paymentFrequency === 12) finalPrice *= 12 * 0.8;

      setTotalPrice(finalPrice.toFixed(2));
    } catch (err) {

    }
  };

  const fetchCropCode = async (cropName) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/api/user/farm-management/get-crop-code`,
        { cropName }
      );

      if (response.data.success) {
        return response.data.cropCode;
      }

      setErrorMessage(
        response.data.message || "Failed to fetch crop code."
      );

      return null;
    } catch (error) {

      setErrorMessage("Failed to fetch crop code. Please try again later.");
      return null;
    }
  };

  const downloadMapScreenshot = async () => {
    if (!polygonCoords.current.length) return null;

    const closedCoords = [
      ...polygonCoords.current,
      polygonCoords.current[0],
    ];

    const pathString = closedCoords
      .map((coord) => `${coord.lat},${coord.lng}`)
      .join("|");

    const visibleCoords = closedCoords
      .map((coord) => `${coord.lat},${coord.lng}`)
      .join("|");

    const customIconUrl =
      "https://firebasestorage.googleapis.com/v0/b/farmbase-b2f7e.appspot.com/o/WebAppAssets%2Fsprout.png?alt=media&token=5979e253-aa32-4270-8bc7-a8d8ee1eff3e";

    const markers = closedCoords
      .map(
        (coord) =>
          `&markers=icon:${encodeURIComponent(
            customIconUrl
          )}|${coord.lat},${coord.lng}`
      )
      .join("");

    const staticMapUrl =
      `https://maps.googleapis.com/maps/api/staticmap?` +
      `visible=${visibleCoords}` +
      `&size=640x640&scale=5` +
      `&maptype=hybrid` +
      `&path=color:0x000000ff|weight:6|fillcolor:0x66bb6a55|${pathString}` +
      markers +
      `&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;

    try {
      const response = await fetch(staticMapUrl);
      const blob = await response.blob();

      const formData = new FormData();
      formData.append("image", blob, "farm_map.png");

      const uploadRes = await fetch(
        `${BASE_URL}/api/user/farm-management/upload-map-image`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await uploadRes.json();

      if (data?.image_url) {

        return data.image_url;
      }
      return null;
    } catch (err) {
      return null;
    }
  };

  const handleSubmit = async () => {
    const errors = {};

    if (!farmName?.trim()) {
      errors.farmName = "Please enter farm name.";
    }

    if (!sowingDate) {
      errors.sowingDate =
        cropType === "plantation"
          ? "Please select pruning date."
          : "Please select sowing date.";
    }

    if (!cropType) {
      errors.cropType = "Please select crop type.";
    }

    if (!selectedCrop) {
      errors.selectedCrop = "Please select a crop.";
    }

    if (!paymentFrequency) {
      errors.paymentFrequency = "Please select payment frequency.";
    }

    if (!polygonCoords.current?.length || !polygonRef.current) {
      errors.map = "Please select your farm area on the map.";
    } else {
      const coordinates = polygonRef.current.getPath().getArray();

      if (coordinates.length < 3) {
        errors.map = "Please select at least 3 points on the map.";
      }
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setErrorMessage("Please fill all the required details.");
      return;
    }

    setErrorMessage("");
    setSpinnerLoading(true); setSpinnerLoading(true);

    try {
      const cropCode = await fetchCropCode(selectedCrop.name);

      if (!cropCode) {
        setSpinnerLoading(false);
        return;
      }

      const polygon = polygonRef.current;

      if (!polygonCoords.current.length) {
        setErrorMessage(
          "Polygon is not defined. Please select the area again."
        );
        setSpinnerLoading(false);
        return;
      }

      const coordinates = polygon
        .getPath()
        .getArray()
        .map((latlng) => ({
          lng: latlng.lng(),
          lat: latlng.lat(),
        }));

      if (coordinates.length < 3) {
        setErrorMessage(
          "Please select at least 3 coordinates for the farm area."
        );
        setSpinnerLoading(false);
        return;
      }

      const areaInHectares = (
        google.maps.geometry.spherical.computeArea(
          polygonRef.current.getPath()
        ) / 10000
      ).toFixed(2);

      const mapImageUrl = await downloadMapScreenshot();

      if (!mapImageUrl) {
        setErrorMessage("Failed to generate map image.");
        setSpinnerLoading(false);
        return;
      }

      const fieldData = {
        CropCode: cropCode,
        FieldName: farmName,
        fieldArea: areaInHectares,
        sowingDate,
        cropType,
        PaymentType: paymentFrequency,
        Points: coordinates,
        SowingDate: sowingDate,
        email: getUserEmail(),
        paymentFrequency,
        plan_name: cropType,
        totalPrice,
        mapImageUrl,
      };

      const response = await axios.post(
        `${BASE_URL}/api/user/farm-management/add-farm`,
        fieldData
      );

      if (response.data.success) {
        navigate("/user/payment", {
          state: {
            cropCode,
            cropName: selectedCrop.name,
            farmName,
            totalPrice,
            cropType,
            sowingDate,
            paymentFrequency,
            coordinates,
            farm_id: response.data.farm_id,
            fieldArea: areaInHectares,
            mapImageUrl,
          },
        });
      } else {
        setErrorMessage(
          response.data.message || "Failed to submit data."
        );
      }
    } catch (error) {

      setErrorMessage("Something went wrong. Please try again later.");
    } finally {
      setSpinnerLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCrop) {
      handleCalculate();
    }
  }, [selectedCrop, selectedArea, paymentFrequency]);

  const getFieldError = (field) => (
    fieldErrors[field] ? (
      <span className="farm-field-error">{fieldErrors[field]}</span>
    ) : null
  );

  return (
    <Box className="add-farm-page">

      {spinnerLoading && (
        <div className="farm-loader-overlay">
          <div className="farm-loader-card">
            <div className="farm-loader-spinner" />

            <span className="farm-loader-eyebrow">
              FARM MANAGEMENT
            </span>

            <h2>Adding your farm...</h2>

            <p>
              Please wait while we save your farm details.
            </p>
          </div>
        </div>
      )}

      <section className="farm-page-intro">
        <div>
          <span className="farm-eyebrow">FARM MANAGEMENT</span>

          <Typography component="h1" className="farm-page-title">
            Add your farm
          </Typography>

          <p className="farm-page-description">
            Map your field and provide the details Farmology needs
            to set up your farm intelligence.
          </p>
        </div>

        <div className="farm-step-indicator">
          <span>01</span>

          <div>
            <strong>New farm</strong>
            <small>Set up your field</small>
          </div>
        </div>
      </section>

      <section className="farm-section-card farm-location-card">

        <div className="farm-section-heading">
          <div>
            <span className="farm-section-eyebrow">
              LOCATION
            </span>

            <h2>Map your farm</h2>

            <p>
              Search for your location, then click on the map to
              mark the boundary of your farm.
            </p>
          </div>

          <div className="farm-map-status">
            <span className="farm-status-dot" />
            Live map
          </div>
        </div>

        <div className="farm-location-search">
          <TextField
            inputRef={searchBoxRef}
            placeholder="Search your farm location"
            fullWidth
            size="small"
            variant="outlined"
            error={Boolean(SearchErrorMessage)}
            helperText={SearchErrorMessage}
            className="farm-text-field"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOnIcon />
                </InputAdornment>
              ),
            }}
          />

          <LoadingButton
            variant="contained"
            onClick={handleGeocode}
            className="farm-search-button"
          >
            Find location
          </LoadingButton>
        </div>

        <div ref={mapRef} className="farm-map" />

        <div className="farm-map-footer">
          <div className="farm-area-display">
            <span className="farm-area-label">
              SELECTED AREA
            </span>

            <strong>
              {selectedArea > 0 ? `${selectedArea} ha` : "—"}
            </strong>
          </div>

          <Button
            variant="outlined"
            onClick={clearAll}
            className="farm-clear-button"
          >
            Clear map
          </Button>
        </div>
      </section>

      <section className="farm-section-card farm-details-card">

        <div className="farm-section-heading">
          <div>
            <span className="farm-section-eyebrow">
              FARM DETAILS
            </span>

            <h2>Tell us about your farm</h2>

            <p>
              Add the crop and season information used to prepare
              your Farmology plan.
            </p>
          </div>
        </div>

        <div className="farm-form">

          <div className="farm-form-field">
            <TextField
              label="Farm name"
              value={farmName}
              onChange={(e) => {
                setFarmName(e.target.value);
                setFieldErrors((prev) => ({ ...prev, farmName: "" }));
              }}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AgricultureIcon />
                  </InputAdornment>
                ),
              }}
              className="farm-text-field"
            />
          </div>


          <TextField
            label={
              cropType === "plantation"
                ? "Pruning date"
                : "Sowing date"
            }
            type="date"
            value={sowingDate}
            onChange={(e) => {
                setSowingDate(e.target.value);
                setFieldErrors((prev) => ({ ...prev, sowingDate: "" }));
              }}
            InputLabelProps={{ shrink: true }}
            fullWidth
            className="farm-form-field farm-text-field"
          />


          <FormControl className="farm-form-field">
            <FormLabel className="farm-form-label">
              Crop type
            </FormLabel>

            <RadioGroup
              row={!isMobile}
              value={cropType}
              onChange={(e) => {
                setCropType(e.target.value);
                setSelectedCrop(null);
                setFieldErrors((prev) => ({
                  ...prev,
                  cropType: "",
                  selectedCrop: "",
                }));
              }}
              className="farm-radio-group"
            >
              <FormControlLabel
                value="plantation"
                control={<Radio />}
                label="Plantation"
              />

              <FormControlLabel
                value="field"
                control={<Radio />}
                label="Field crop"
              />
            </RadioGroup>
            {getFieldError("cropType")}
          </FormControl>


          <FormControl className="farm-form-field">
            <FormLabel className="farm-form-label">
              Select crop
            </FormLabel>

            {loading ? (
              <div className="farm-crop-loading">
                <CircularProgress size={22} />
                <span>Loading crops...</span>
              </div>
            ) : (
              <Select
                value={selectedCrop?.name || ""}
                onChange={(e) => {
                  setSelectedCrop(
                    crops.find((c) => c.name === e.target.value)
                  );
                  setFieldErrors((prev) => ({
                    ...prev,
                    selectedCrop: "",
                  }));
                }}
                displayEmpty
                className="farm-select"
              >
                {crops.length === 0 ? (
                  <MenuItem disabled>
                    No crops available
                  </MenuItem>
                ) : (
                  crops.map((crop) => (
                    <MenuItem
                      key={crop.id}
                      value={crop.name}
                    >
                      {crop.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            )}
              {getFieldError("selectedCrop")}
          </FormControl>

          <FormControl className="farm-form-field farm-field-full farm-payment-field">
            <FormLabel className="farm-form-label">
              Payment frequency
            </FormLabel>

            <RadioGroup
              row={!isMobile}
              value={paymentFrequency}
              onChange={(e) => {
                setPaymentFrequency(parseInt(e.target.value));
                setFieldErrors((prev) => ({
                  ...prev,
                  paymentFrequency: "",
                }));
              }}
              className="farm-payment-options"
            >
              <FormControlLabel
                value={1}
                control={<Radio />}
                label={
                  <span className="farm-payment-option">
                    <strong>Monthly</strong>
                    <small>Standard billing</small>
                  </span>
                }
              />

              <FormControlLabel
                value={3}
                control={<Radio />}
                label={
                  <span className="farm-payment-option">
                    <strong>Quarterly</strong>
                    <small>10% off</small>
                  </span>
                }
              />

              <FormControlLabel
                value={12}
                control={<Radio />}
                label={
                  <span className="farm-payment-option">
                    <strong>Yearly</strong>
                    <small>20% off</small>
                  </span>
                }
              />
            </RadioGroup>
            {getFieldError("paymentFrequency")}
          </FormControl>

          {totalPrice > 0 && (
            <div className="farm-total-card">
              <div>
                <span>Total estimated cost</span>
                <strong>₹{totalPrice}</strong>
              </div>

              <small>Including GST</small>
            </div>
          )}



          {errorMessage && (
            <div className="farm-error-message">
              <strong>Unable to continue</strong>
              <span>{errorMessage}</span>
            </div>
          )}

          <div className="farm-submit-wrap">
            <span className="farm-submit-tooltip-wrap">
              <LoadingButton
                variant="contained"
                loading={spinnerLoading}
                onClick={handleSubmit}
                fullWidth
                disabled={spinnerLoading}
                className="farm-save-button"
              >
                Save farm
              </LoadingButton>
            </span>
          </div>

        </div>
      </section>
    </Box>
  );
};

export default AddFarmPage1;
